import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import { ArrowsClockwise, CheckCircle, XCircle, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EmailLogPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/log");
      setLogs(data);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/email/log");
        if (!cancelled) setLogs(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isFailedish = (s) => s && s !== "sent";

  // Show a warning when many recent rows are "suppressed" — usually means the
  // `BUSINESS_GRABBER_DISABLE_OUTBOUND_EMAIL` env var is set on the running
  // backend. Safe in preview, *bad* in production.
  const suppressedCount = logs.filter((l) => l.status === "suppressed").length;
  const showSuppressionWarning = suppressedCount >= 3;

  const retriableIds = useMemo(
    () => logs.filter((l) => isFailedish(l.status)).map((l) => l.id),
    [logs],
  );
  const allSelected = retriableIds.length > 0 && retriableIds.every((id) => selected.has(id));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(retriableIds));
  };

  const retry = async (l) => {
    setRetryingId(l.id);
    try {
      const { data } = await api.post(`/email/log/${l.id}/retry`);
      if (data.ok) {
        toast.success(`Re-sent to ${data.to.join(", ")}`);
      } else {
        toast.error(`Graph rejected: ${data.error}`, { duration: 12000 });
      }
      const list = await api.get("/email/log").then((r) => r.data);
      setLogs(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  const retrySelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Retry ${ids.length} email${ids.length === 1 ? "" : "s"} now?`)) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/email/log/retry-bulk", { ids });
      const { succeeded = 0, failed = 0, results = [] } = data || {};
      if (failed === 0) {
        toast.success(`Re-sent ${succeeded} email${succeeded === 1 ? "" : "s"}`);
      } else if (succeeded === 0) {
        const firstErr = results.find((r) => !r.ok)?.error || "unknown error";
        toast.error(`All ${failed} retries failed. First error: ${firstErr}`, { duration: 14000 });
      } else {
        toast.warning(`Re-sent ${succeeded}, ${failed} still failing. Inspect inline error text.`, { duration: 10000 });
      }
      const list = await api.get("/email/log").then((r) => r.data);
      setLogs(list);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk retry failed");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-tracking">Activity</div>
          <h3 className="font-display text-2xl text-slate-900 mt-1">Email Log</h3>
          <p className="text-sm text-slate-500 mt-2">Recent automated and test emails sent by the system.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button
              data-testid="bulk-retry-btn"
              onClick={retrySelected}
              disabled={bulkRunning}
              size="sm"
              className="rounded-sm gap-2 bg-slate-900 hover:bg-slate-800"
            >
              <ArrowClockwise size={14} className={bulkRunning ? "animate-spin" : ""} />
              {bulkRunning ? "Retrying…" : `Retry selected (${selected.size})`}
            </Button>
          )}
          <Button
            data-testid="refresh-log-btn"
            onClick={fetchLogs}
            variant="outline"
            size="sm"
            className="rounded-sm gap-2"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>
      {showSuppressionWarning && (
        <div data-testid="suppression-warning" className="mx-6 mt-4 p-3 border border-amber-300 bg-amber-50 rounded-sm text-xs text-amber-900 flex gap-2">
          <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b>Outbound email is suppressed on this backend.</b> {suppressedCount} recent entries were recorded but never sent. This is the
            expected behaviour on the preview environment — on the production deployment it means the env var
            <code className="mx-1 px-1 bg-amber-100 rounded">BUSINESS_GRABBER_DISABLE_OUTBOUND_EMAIL</code>
            is set to <code className="mx-1 px-1 bg-amber-100 rounded">true</code>.
            Remove it (or set it to <code className="mx-1 px-1 bg-amber-100 rounded">false</code>) on your production deployment and redeploy.
          </div>
        </div>
      )}
      {logs.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="email-log-empty">
          No emails sent yet. Configure email routing and assign a lead to trigger the first notification.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="email-log-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/50 text-left">
              <th className="p-3 w-8">
                {retriableIds.length > 0 && (
                  <input
                    type="checkbox"
                    data-testid="select-all"
                    checked={allSelected}
                    onChange={toggleAll}
                    title={allSelected ? "Deselect all" : "Select all retriable rows"}
                    className="cursor-pointer"
                  />
                )}
              </th>
              <th className="p-3 label-tracking !tracking-[0.18em] font-medium">When</th>
              <th className="p-3 label-tracking !tracking-[0.18em] font-medium">Trigger</th>
              <th className="p-3 label-tracking !tracking-[0.18em] font-medium">To</th>
              <th className="p-3 label-tracking !tracking-[0.18em] font-medium">Subject</th>
              <th className="p-3 label-tracking !tracking-[0.18em] font-medium">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-b border-slate-100" data-testid={`email-log-row-${l.id}`}>
                  <td className="p-3">
                    {isFailedish(l.status) && (
                      <input
                        type="checkbox"
                        data-testid={`select-${l.id}`}
                        checked={selected.has(l.id)}
                        onChange={() => toggle(l.id)}
                        className="cursor-pointer"
                      />
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{formatDate(l.sent_at)}</td>
                  <td className="p-3 text-xs font-mono text-slate-700">{l.trigger || "—"}</td>
                  <td className="p-3 text-xs text-slate-700">{l.to}</td>
                  <td className="p-3 text-xs text-slate-900 max-w-md truncate">{l.subject}</td>
                  <td className="p-3">
                    {l.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle size={12} weight="fill" /> Sent
                      </span>
                    ) : l.status === "suppressed" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="Preview mode — outbound mail suppressed">
                        <CheckCircle size={12} weight="fill" /> Suppressed
                      </span>
                    ) : l.status === "skipped" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700" title={l.error || l.message}>
                        <WarningCircle size={12} weight="fill" /> Skipped
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-rose-700" title={l.error}>
                        <XCircle size={12} weight="fill" /> Failed
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {isFailedish(l.status) && (
                      <button
                        data-testid={`retry-${l.id}`}
                        disabled={retryingId === l.id || bulkRunning}
                        onClick={() => retry(l)}
                        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-40"
                        title="Retry this send and show the Graph response"
                      >
                        <ArrowClockwise size={12} className={retryingId === l.id ? "animate-spin" : ""} />
                        {retryingId === l.id ? "Retrying…" : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
                {isFailedish(l.status) && (
                  <tr className="border-b border-slate-100 bg-rose-50/40" data-testid={`email-log-error-${l.id}`}>
                    <td className="p-3"></td>
                    <td colSpan={6} className="p-3 text-[11px] text-rose-800 font-mono whitespace-pre-wrap break-all">
                      {l.status === "suppressed"
                        ? "Outbound email is suppressed on this backend (BUSINESS_GRABBER_DISABLE_OUTBOUND_EMAIL=true). The email was never actually sent. Click Retry to force-send it now after fixing the env var."
                        : (l.error || l.message || "(no diagnostic recorded — old log entry. Click Retry to capture the live error.)")
                      }
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
